import React, { useState, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiFileText, FiSearch, FiX, FiRefreshCw, FiCalendar, FiBriefcase, FiEye, FiInfo, FiCheckCircle
} from "react-icons/fi";
import "./EmployeeDashboard.css";

const AppliedJobs = () => {
  const { appliedJobs, searchQuery: globalSearchQuery } = useOutletContext();
  const navigate = useNavigate();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const statuses = ["All", "Selected", "Rejected", "Pending", "Interview"];

  const getStatusStyles = (status) => {
    switch (status) {
      case "Selected": case "Hired":
        return "bg-green-100 text-green-700";
      case "Rejected":
        return "bg-red-100 text-red-700";
      case "Assessment Shared": case "Interview Scheduled":
        return "bg-indigo-100 text-indigo-700";
      case "Resigned":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  // Simple fuzzy matching allowing up to one character difference
  const fuzzyMatch = (source, query) => {
    if (!source) return false;
    const s = source.toLowerCase();
    const q = query.toLowerCase();
    if (s.includes(q)) return true;
    // quick Levenshtein distance limited to 1
    let i = 0,
      j = 0,
      diff = 0;
    while (i < s.length && j < q.length) {
      if (s[i] === q[j]) {
        i++;
        j++;
      } else {
        diff++;
        i++; // skip a character in source
        if (diff > 1) return false;
      }
    }
    // account for remaining characters
    diff += q.length - j;
    return diff <= 1;
  };

  const filteredApps = appliedJobs.filter(app => {
    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    const matchesSearch =
      fuzzyMatch(app.jobId?.role || app.role || "", combinedQuery) ||
      fuzzyMatch(app.status || "", combinedQuery);

    let matchesStatus = true;
    if (statusFilter) {
      const appStatus = (app.status || "").toLowerCase();
      if (statusFilter === "All") {
        matchesStatus = true;
      } else if (statusFilter === "Pending") {
        matchesStatus = !["selected", "rejected", "interview scheduled", "interview"].includes(appStatus) && !appStatus.includes("interview");
      } else if (statusFilter === "Interview") {
        matchesStatus = ["interview scheduled", "interview"].includes(appStatus) || appStatus.includes("interview");
      } else {
        matchesStatus = appStatus === statusFilter.toLowerCase();
      }
    }

    let matchesDate = true;
    if (dateFilter) {
      const appDate = new Date(app.createdAt || app.appliedAt).toISOString().split("T")[0];
      matchesDate = appDate === dateFilter;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const resetFilters = () => {
    setLocalSearchQuery("");
    setStatusFilter("");
    setDateFilter("");
  };

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              My <span>Applications</span>
            </h1>
            <p className="emp-dash__subtitle">
              Track your job application status
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiFileText />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Filter Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiSearch className="text-green-600" /> Filter Applications
              </h3>
              <p className="emp-dash__card-desc">Search by role, status, or date</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search roles or status..."
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-300 bg-white"
                />
                {localSearchQuery && (
                  <FiX
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer hover:text-gray-700"
                    onClick={() => setLocalSearchQuery("")}
                  />
                )}
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-11 px-4 text-sm font-semibold border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
              >
                <option value="">All Statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>

              {/* Date Filter */}
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="h-11 px-4 text-sm font-semibold border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
              />

              {/* Reset */}
              {(localSearchQuery || statusFilter || dateFilter) && (
                <button
                  onClick={resetFilters}
                  className="h-11 px-5 text-sm font-semibold text-gray-700 bg-white border-2 border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-md flex items-center gap-2"
                >
                  <FiRefreshCw className="text-sm" /> Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="emp-dash__card py-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiBriefcase className="text-green-600" /> Application History
              </h3>
              <p className="emp-dash__card-desc">Showing {filteredApps.length} applications</p>
            </div>
          </div>
          {filteredApps.length > 0 ? (
            <div className="emp-dash__table-wrap overflow-x-auto !block">
              <table className="emp-dash__table min-w-[800px]">
                <thead>
                  <tr>
                    <th className="py-3 px-4 text-center font-bold">Application</th>
                    <th className="py-3 px-4 text-center font-bold">Applied On</th>
                    <th className="py-3 px-4 text-center font-bold">Current Status</th>
                    <th className="py-3 px-4 text-center font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app) => (
                    <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 text-center">
                        <div className="text-xs font-bold text-gray-800">{app.jobId?.role || app.role || "N/A"}</div>
                        <div className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Ref: {app._id.slice(-6).toUpperCase()}</div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="text-[10px] font-medium text-gray-500">
                          {new Date(app.createdAt || app.appliedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${getStatusStyles(app.status)}`}>
                            {app.status || "Applied"}
                          </span>
                          {(app.interviewStatus === "Invited" || app.interviewStatus === "Rescheduled") && app.candidateInterviewStatus && app.candidateInterviewStatus !== "Pending" && (
                            <span className={`text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${app.candidateInterviewStatus === "Confirmed" ? "bg-indigo-50 text-indigo-600 border border-indigo-100" : "bg-red-50 text-red-600 border border-red-100"}`}>
                              {app.candidateInterviewStatus}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-3">
                          {/* Assessment Actions - Sequential Flow */}
                          {app.jobId && (app.jobId.assessmentId || (app.jobId.assessmentIds && app.jobId.assessmentIds.length > 0)) ? (() => {
                            // Collect all assessment IDs linked to this job (in order)
                            const allAssessmentIds = [];
                            if (app.jobId.assessmentIds && app.jobId.assessmentIds.length > 0) {
                              app.jobId.assessmentIds.forEach(a => {
                                const id = a?._id || a;
                                if (id) allAssessmentIds.push(String(id));
                              });
                            } else if (app.jobId.assessmentId) {
                              const id = app.jobId.assessmentId?._id || app.jobId.assessmentId;
                              if (id) allAssessmentIds.push(String(id));
                            }

                            // Find which ones the candidate has already completed
                            const completedIds = (app.assessmentResults || []).map(r =>
                              String(r.quizId?._id || r.quizId)
                            );

                            // Find only the FIRST pending assessment (sequential)
                            const nextPendingId = allAssessmentIds.find(id => !completedIds.includes(id));
                            const doneCount = completedIds.filter(id => allAssessmentIds.includes(id)).length;
                            const totalCount = allAssessmentIds.length;
                            const allDone = !nextPendingId && totalCount > 0;

                            if (allDone) {
                              return (
                                <div className="flex flex-col items-center">
                                  <span className="text-[10px] font-black text-emerald-600 flex items-center gap-1.5 whitespace-nowrap">
                                    <FiCheckCircle className="text-[11px]" /> ALL VERIFIED
                                  </span>
                                  {doneCount > 1 && (
                                    <span className="text-[9px] font-bold text-gray-400">{doneCount}/{totalCount} tests</span>
                                  )}
                                </div>
                              );
                            }

                            if (nextPendingId) {
                              const nextIdx = allAssessmentIds.indexOf(nextPendingId) + 1;
                              return (
                                <div className="flex flex-col items-center gap-0.5">
                                  {doneCount > 0 && (
                                    <span className="text-[9px] font-bold text-emerald-500">{doneCount}/{totalCount} Done ✓</span>
                                  )}
                                  <button
                                    onClick={() => navigate(`/take-assessment/${app.jobId._id}/${app._id}/${nextPendingId}`)}
                                    className="px-0 bg-transparent text-blue-600 text-[10px] font-bold uppercase tracking-widest hover:text-blue-800 transition-all flex items-center gap-1.5 group/test whitespace-nowrap"
                                  >
                                    <FiRefreshCw size={12} className="group-hover/test:rotate-180 transition-transform duration-500" />
                                    Test {nextIdx}{totalCount > 1 ? ` of ${totalCount}` : ""}
                                  </button>
                                </div>
                              );
                            }

                            return null;
                          })() : null}
                          <button
                            onClick={() => navigate(`/jobs/${app.jobId?._id}`)}
                            className="w-9 h-9 bg-transparent text-blue-600 hover:text-blue-800 transition-all active:scale-95 group flex items-center justify-center"
                            title="View Details"
                          >
                            <FiEye className="text-xl transition-transform group-hover:scale-110" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-10 text-center">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-300">
                  <FiBriefcase size={32} />
                </div>
                <span className="text-sm font-medium text-gray-500">No applications found matching your filters</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiInfo className="text-blue-400" />
          Timely Health Recruitment Tracker • {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default AppliedJobs;